import React, { useEffect, useState } from "react";
import { isOnline, onNetChange } from "../services/net";
import { onQueueChange, count as queueCount } from "../services/offlineQueue";
import { flushOutbox } from "../services/api";
import { useT } from "../i18n/useT";

// Slim status strip shown app-wide during an internet outage or while queued
// actions are waiting to sync (Directive 1142/2026 business continuity).
const S = {
  en: {
    offline: "Working offline — actions are saved and will sync when the connection returns.",
    pending_one: "1 action waiting to sync.",
    pending_many: "{n} actions waiting to sync.",
    sync_now: "Sync now",
    failed: "Some actions could not be synced — please check the front desk list.",
  },
  am: {
    offline: "ከመስመር ውጭ እየሰሩ ነው — ተግባራት ተቀምጠዋል፣ ግንኙነት ሲመለስ ይመሳሰላሉ።",
    pending_one: "1 ተግባር ለማመሳሰል በመጠባበቅ ላይ።",
    pending_many: "{n} ተግባራት ለማመሳሰል በመጠባበቅ ላይ።",
    sync_now: "አሁን አመሳስል",
    failed: "አንዳንድ ተግባራት ማመሳሰል አልተቻለም — እባክዎ የመቀበያ ዝርዝሩን ያረጋግጡ።",
  },
  om: {
    offline: "Toorarraa ala hojjechaa jirta — gochaaleen ni kuufaman, yeroo walqunnamtiin deebi'u ni walsimu.",
    pending_one: "Gochi 1 walsimuuf eeggachaa jira.",
    pending_many: "Gochaaleen {n} walsimuuf eeggachaa jiru.",
    sync_now: "Amma walsimsiisi",
    failed: "Gochaaleen tokko tokko walsimuu hin dandeenye — maaloo tarree simannaa ilaali.",
  },
};

export default function OfflineBanner() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const refresh = () => queueCount().then(setPending).catch(() => {});
    refresh();
    const offNet = onNetChange(setOnline);
    const offQ = onQueueChange(refresh);
    const onFail = () => setFailed(true);
    window.addEventListener("odra:sync-failures", onFail);
    return () => { offNet(); offQ(); window.removeEventListener("odra:sync-failures", onFail); };
  }, []);

  if (online && pending === 0 && !failed) return null;

  const bg = !online ? "#854F0B" : failed ? "#A32D2D" : "#0F6E56";
  const msg = !online
    ? t("offline")
    : failed
    ? t("failed")
    : pending === 1
    ? t("pending_one")
    : t("pending_many").replace("{n}", String(pending));

  return (
    <div style={{ background: bg, color: "#fff", fontSize: 13, padding: "7px 14px", display: "flex", alignItems: "center", gap: 12, justifyContent: "center", position: "sticky", top: 0, left: 0, right: 0, width: "100%", boxSizing: "border-box", zIndex: 200 }}>
      <span style={{ display: "inline-flex", width: 8, height: 8, borderRadius: "50%", background: "#fff", opacity: online ? 1 : 0.6 }} />
      <span>{msg}</span>
      {online && pending > 0 && (
        <button onClick={() => { setFailed(false); flushOutbox().then(() => queueCount().then(setPending)); }}
          style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.5)", borderRadius: 6, fontSize: 12, padding: "2px 10px", cursor: "pointer" }}>
          {t("sync_now")}
        </button>
      )}
    </div>
  );
}
